<script setup>
  import {computed, onBeforeMount, ref, useSlots, watch} from "vue";

  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "ComponentSelect",
  });

  const slots = useSlots();

  const props = defineProps({
    options: {
      type: [Array, Object],
      default: () => {
        return [];
      },
    },
    label: {
      type: String,
      default: "",
    },
    validation: {
      type: String,
      default: null,
    },
    helperText: {
      type: String,
      default: null,
    },
    background: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showPlaceholderOption: {
      type: Boolean,
      default: true,
    },
    placeholderText: {
      type: String,
      default: "Bitte auswählen",
    },
    nullable: {
      type: Boolean,
      default: false,
    },
    keyName: {
      type: String,
      default: "label",
    },
    keyValue: {
      type: String,
      default: "value",
    },
    modelValue: {
      type: [String, Number, Object],
      default: "",
    },
    returnType: {
      type: String,
      default: "value",
    },
    large: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: true,
    },
  });

  const emit = defineEmits(["change", "update:modelValue"]);

  const fieldId = ref(crypto.randomUUID());
  const selected = ref(null);

  const hasIconSlot = computed(() => !!slots.icon);

  const isInvalid = computed(() => props.validation);

  const optionList = computed(() => {
    if (!props.options) {
      return [];
    }

    if (Array.isArray(props.options)) {
      return props.options;
    }

    let options = [];
    Object.keys(props.options).forEach((key) => {
      if (props.options[key] === "") {
        options.unshift(props.options[key]);
      } else {
        options.push({[props.keyValue]: key, [props.keyName]: props.options[key]});
      }
    });

    return options;
  });

  watch(
    () => props.modelValue,
    (newValue) => {
      handleUpdateValue(newValue);
    },
  );

  onBeforeMount(() => {
    handleUpdateValue(props.modelValue);
  });

  const handleUpdateValue = (newValue) => {
    if (newValue || !Number.isNaN(Number.parseFloat(newValue))) {
      if (newValue instanceof Object) {
        selected.value = newValue[props.keyValue];
      } else {
        selected.value = newValue;
      }
    } else {
      selected.value = "";
    }
  };

  const onChange = (event) => {
    const value = selected.value === "" ? null : selected.value;
    const text = event.target.options[event.target.options.selectedIndex].text;

    if (props.returnType === "object") {
      if (Array.isArray(props.options)) {
        const payload = props.options.find((option) => option[props.keyName] === text);
        emit("update:modelValue", payload);
        emit("change", payload);
      } else {
        emit("update:modelValue", {[props.keyName]: text, [props.keyValue]: value});
        emit("change", {[props.keyName]: text, [props.keyValue]: value});
      }
    } else {
      emit("update:modelValue", value);
      emit("change", value);
    }
  };
</script>

<template>
  <div :class="cn('inline-block', {'w-full': fullWidth})">
    <template v-if="label">
      <label
        :class="{
          'text-mcred': isInvalid,
          'text-gray-500': !isInvalid && !large,
          'text-sm': !large,
        }"
        class="origin-0 -z-1 truncate duration-300"
        :for="fieldId"
        :data-test="getTestId('select-label')"
      >
        {{ label }}
      </label>
    </template>

    <div class="relative flex" :class="background">
      <div v-if="hasIconSlot" class="absolute ml-1 pt-2">
        <slot name="icon" />
      </div>

      <select
        :id="fieldId"
        v-model="selected"
        class="h-9 w-full items-center border-0 border-b-1 border-gray-300 bg-transparent pr-6 text-left text-ellipsis focus:border-mcred focus:ring-0"
        :class="{
          'px-2': !hasIconSlot,
          'pr-2 pl-8': hasIconSlot,
        }"
        :disabled="disabled"
        :data-test="getTestId('select')"
        @change="onChange"
      >
        <option
          v-if="showPlaceholderOption"
          :disabled="!nullable"
          value=""
          :data-test="getTestId('select-option-placeholder')"
        >
          {{ placeholderText }}
        </option>

        <template v-if="options || options.length > 0">
          <option
            v-for="(option, index) in optionList"
            :key="index"
            :value="option[keyValue]"
            :data-test="getTestId(`select-option-${index}`)"
          >
            {{ option[keyName] }}
          </option>
        </template>

        <slot v-if="!options || options.length === 0" name="default" />
      </select>
    </div>

    <div v-if="validation" class="mt-1.5 text-xs text-mcred" :data-test="getTestId('select-error-message')">
      {{ validation }}
    </div>

    <div
      v-if="helperText && !isInvalid"
      class="mt-1.5 text-xs text-gray-500"
      :data-test="getTestId('select-helper-text')"
    >
      {{ helperText }}
    </div>
  </div>
</template>
